import { useMemo, useState } from "react";
import { Pressable, Text, TextInput, View } from "react-native";
import { useTranslation } from "react-i18next";
import { MAX_CYCLE_WEEKS, MAX_SESSIONS, type NewProgram } from "@jackedlogs/shared";

import { resolveTheme } from "../theme";
import { useThemePreferenceContext } from "../ThemeContext";
import { Stepper } from "../primitives/Stepper";
import { makeSetupStyles } from "./setupStyles";

// Create-from-scratch stage: the one screen of choices seeded at creation
// (weeks and training days — both now also editable later in the composer,
// PE-11, via the same shared <Stepper> + add/remove-day). Name + two steppers,
// then the composer opens on an empty grid. Sessions are seeded D1..Dn; every
// day needs at least one exercise before save (slots.min(1) — the schema floor).

export function NewProgramSetup({
  dark,
  onCreate,
  onCancel,
}: {
  dark: boolean;
  onCreate: (seed: NewProgram) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const family = useThemePreferenceContext()?.preference.family ?? "brand";
  const theme = resolveTheme(dark, family);
  const styles = useMemo(() => makeSetupStyles(theme), [theme]);
  const [name, setName] = useState("");
  const [weeks, setWeeks] = useState(4);
  const [days, setDays] = useState(3);
  const canCreate = name.trim().length > 0;

  const create = () => {
    if (!canCreate) return;
    onCreate({
      name: name.trim(),
      cycleWeeks: weeks,
      sessionsPerWeek: days,
      sessions: Array.from({ length: days }, (_, i) => ({
        name: `D${i + 1}`,
        slots: [],
      })),
    } as NewProgram);
  };

  return (
    <View style={styles.container}>
      <Text style={styles.title}>
        {t("program.newTitle", { defaultValue: "New program" })}
      </Text>

      <Text style={styles.fieldLabel}>
        {t("program.name", { defaultValue: "Name" })}
      </Text>
      <TextInput
        style={styles.input}
        value={name}
        onChangeText={setName}
        placeholder={t("program.namePlaceholder", {
          defaultValue: "e.g. Powerbuilding 6-week",
        })}
        accessibilityLabel={t("program.name", { defaultValue: "Name" })}
      />

      <Stepper
        theme={theme}
        label={t("composer.weeks", { defaultValue: "Weeks" })}
        value={weeks}
        min={1}
        max={MAX_CYCLE_WEEKS}
        onChange={setWeeks}
      />
      <Stepper
        theme={theme}
        label={t("composer.days", { defaultValue: "Days" })}
        value={days}
        min={1}
        max={MAX_SESSIONS}
        onChange={setDays}
      />
      <Text style={styles.hint}>
        {t("composer.setupHint", {
          defaultValue:
            "Set the starting shape — you can add or remove days and change the weeks later in the editor.",
        })}
      </Text>

      <Pressable
        accessibilityRole="button"
        disabled={!canCreate}
        onPress={create}
        style={[styles.createBtn, !canCreate && styles.createBtnDisabled]}
      >
        <Text
          style={[styles.createBtnText, !canCreate && styles.createBtnTextDisabled]}
        >
          {t("program.create", { defaultValue: "Create" })}
        </Text>
      </Pressable>
      <Pressable
        accessibilityRole="button"
        onPress={onCancel}
        style={styles.cancelBtn}
      >
        <Text style={styles.cancelText}>
          {t("program.cancel", { defaultValue: "Cancel" })}
        </Text>
      </Pressable>
    </View>
  );
}
